// @ts-nocheck
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface ArticleMention {
  id: string;
  article_id: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  approved_at?: string;
  rejected_at?: string;
}

interface Article {
  id: string;
  title: string;
  content: string;
  author_id: string;
  created_at: string;
  published_at?: string;
  featured_image_url?: string;
}

interface Video {
  id: string;
  title: string;
  description?: string;
  membership_slug: string;
  created_at: string;
}

interface MentionWithArticle {
  mention: ArticleMention;
  article: Article | null;
  authorName: string;
}

export const useAthleteMentions = (sourceType: 'video' = 'video', publicProfileId?: string) => {
  const { user, profile } = useAuth();
  const [mentions, setMentions] = useState<MentionWithArticle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMentions = useCallback(async () => {
    // For public profiles, use publicProfileId; for authenticated users, use their profile
    const targetProfileId = publicProfileId || profile?.id;
    
    if (!targetProfileId) {
      console.log('🚫 No profile ID found for mentions');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // profile_mentions table doesn't exist in RLevents
      setMentions([]);
      return;
      
      // If viewing public profile (not own profile), only show approved mentions
      if (publicProfileId && publicProfileId !== profile?.id) {
        mentionsQuery = mentionsQuery.eq('status', 'approved');
      }
      
      const { data: mentionsData, error: mentionsError } = await mentionsQuery
        .order('created_at', { ascending: false });

      if (mentionsError) {
        console.error('❌ Error fetching mentions:', mentionsError);
        throw mentionsError;
      }

      console.log('✅ Found mentions:', mentionsData?.length || 0);

      if (!mentionsData || mentionsData.length === 0) {
        setMentions([]);
        return;
      }

      // Step 2: Get unique source IDs to fetch
      const sourceIds = [...new Set(mentionsData.map(m => m.source_id))];
      console.log(`📄 Fetching ${sourceType}s for IDs:`, sourceIds);

      let sourceData: (Article | Video)[] = [];
      let authorsData: any[] = [];

      // Fetch videos
      const { data: videosData, error: videosError } = await supabase
        .from('videos')
        .select('id, title, description, membership_slug, created_at')
        .in('id', sourceIds);

      if (videosError) {
        console.error('❌ Error fetching videos:', videosError);
      } else {
        sourceData = videosData || [];
      }

      // Step 3: Combine mentions with source content and author names
      const mentionsWithSources: MentionWithArticle[] = mentionsData.map(mention => {
        const source = sourceData.find(s => s.id === mention.source_id);
        
        const authorName = 'Admin'; // Videos are created by admins
        
        // Normalize the source data to match Article interface
        const article: Article | null = source ? {
          id: source.id,
          title: source.title,
          content: (source as Video).description || '',
          author_id: '',
          created_at: source.created_at,
          published_at: source.created_at,
          featured_image_url: undefined
        } : null;
        
        return {
          mention: {
            id: mention.id,
            article_id: mention.source_id,
            status: mention.status,
            created_at: mention.created_at,
            approved_at: mention.approved_at,
            rejected_at: mention.rejected_at
          },
          article,
          authorName
        };
      });

      console.log(`✅ Combined mentions with ${sourceType}s:`, mentionsWithSources.length);
      setMentions(mentionsWithSources);

    } catch (err) {
      console.error('❌ Error in fetchMentions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load mentions');
      toast.error('Failed to load video mentions');
    } finally {
      setLoading(false);
    }
  }, [publicProfileId, profile?.id, sourceType]);

  const updateMentionStatus = useCallback(async (mentionId: string, status: 'approved' | 'rejected') => {
    const targetProfileId = publicProfileId || profile?.id;
    
    if (!user?.id || !targetProfileId) {
      console.error('❌ No user ID or profile ID found for mention status update');
      return false;
    }

    try {
      setLoading(true);
      console.log(`🔄 Updating mention ${mentionId} status to ${status}`);

      const updateData: any = {
        status,
        [`${status}_at`]: new Date().toISOString()
      };

      const { error } = await supabase
        .from('profile_mentions')
        .update(updateData)
        .eq('id', mentionId)
        .eq('profile_id', targetProfileId); // Extra safety check

      if (error) {
        console.error(`❌ Error updating mention status:`, error);
        toast.error(`Failed to ${status} mention`);
        return false;
      }

      console.log(`✅ Mention ${status} successfully`);
      toast.success(`Mention ${status} successfully`);
      
      // Refresh mentions after status update
      await fetchMentions();
      return true;

    } catch (error) {
      console.error(`❌ Exception updating mention status:`, error);
      toast.error(`Failed to ${status} mention`);
      return false;
    } finally {
      setLoading(false);
    }
  }, [user?.id, publicProfileId, profile?.id, fetchMentions]);

  useEffect(() => {
    const targetProfileId = publicProfileId || profile?.id;
    if (targetProfileId) {
      fetchMentions();
    }
  }, [publicProfileId, profile?.id, fetchMentions]);

  return {
    mentions,
    loading,
    error,
    fetchMentions,
    updateMentionStatus
  };
};